import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Building2 } from 'lucide-react';
import { listBranches } from '../../api/branches';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/Button';
import { BranchCard } from '../../components/branches/BranchCard';

const FILTERS = [
  ['active', 'Aktif'],
  ['deleted', 'Silinmiş'],
];

export function BranchesListPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();

  const [branches, setBranches] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('active');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    listBranches({ is_active: filter === 'active' ? 'true' : 'false', page })
      .then((d) => { setBranches(d.branches); setPagination(d.pagination); })
      .catch((err) => setError(err.response?.data?.error || 'Şubeler yüklenemedi'))
      .finally(() => setLoading(false));
  }, [page, filter]);

  const changeFilter = (val) => {
    if (val === filter) return;
    setFilter(val);
    setPage(1);
  };

  const total = pagination?.total ?? branches.length;
  const isDeletedView = filter === 'deleted';

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Başlık */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Şubeler</h1>
          <p className="text-slate-500 text-sm mt-0.5">{total} şube</p>
        </div>
        {isAdmin && (
          <Button onClick={() => navigate('/settings/branches/new')}>
            <Plus size={16} /> Yeni Şube
          </Button>
        )}
      </div>

      {/* Aktif / Silinmiş filtre toggle */}
      <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
        {FILTERS.map(([val, label]) => (
          <button
            key={val}
            onClick={() => changeFilter(val)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              filter === val
                ? 'bg-white text-slate-800 shadow-sm font-medium'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <p className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
      ) : branches.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Building2 size={40} className="mx-auto mb-3 opacity-40" />
          <p>{isDeletedView ? 'Silinmiş şube yok' : 'Henüz şube yok'}</p>
          {isAdmin && !isDeletedView && (
            <Button className="mt-4" onClick={() => navigate('/settings/branches/new')}>
              <Plus size={16} /> Yeni Şube
            </Button>
          )}
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            {branches.map((b) => (
              <BranchCard key={b.id} branch={b} isAdmin={isAdmin} />
            ))}
          </div>

          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Önceki
              </Button>
              <span className="text-sm text-slate-500">
                Sayfa {pagination.page} / {pagination.totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Sonraki
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
